import React, { useState, useEffect } from 'react';
import './Place.css';
import { useOutletContext } from 'react-router-dom';
import GeographicalMap, { MapPlace, MapState } from '../components/map/GeographicalMap';
import LocationInfo from '../components/map/utils/LocationInfo';
import RadiusControl from '../components/map/utils/RadiusControl';
import GeolocationWarning from '../components/map/utils/GeolocationWarning';
import { fetchPlaces } from '../services/placeService';
import { getPageTitle } from '../constants';

const PlacePage: React.FC = () => {
  const { translations } = useOutletContext<{ translations: Record<string, string> }>();
  const [mapState, setMapState] = useState<MapState | null>(null);
  const [selectedPlaceType, setSelectedPlaceType] = useState('');
  const [places, setPlaces] = useState<MapPlace[]>([]);

  useEffect(() => {
    document.title = getPageTitle('Place');
  }, []);

  const userLat = mapState?.userLocation?.[0];
  const userLng = mapState?.userLocation?.[1];
  const searchRadius = mapState?.searchRadius;

  useEffect(() => {
    const loadPlaces = async () => {
      if (userLat === undefined || userLng === undefined || !searchRadius) {
        return;
      }

      try {
        const fetchedPlaces = await fetchPlaces({
          lat: userLat,
          lng: userLng,
          radius: searchRadius,
          placeType: selectedPlaceType,
        });
        setPlaces(fetchedPlaces);
      } catch (error) {
        console.error('Failed to fetch places:', error);
      }
    };

    loadPlaces();
  }, [selectedPlaceType, userLat, userLng, searchRadius]);

  const handlePlaceClick = (place: MapPlace) => {
    console.log('Place clicked:', place);
  };

  return (
    <>
      <div className="page-content">
        <div className="search-panel">
          {mapState && (
            <>
              <LocationInfo
                userLocation={mapState.userLocation}
                locationAddress={mapState.locationAddress}
                translations={translations}
              />
              <RadiusControl
                searchRadius={mapState.searchRadius}
                onRadiusChange={mapState.onRadiusChange}
                translations={translations}
              />
            </>
          )}

          <div className="search-form">
            <div className="form-group">
              <label htmlFor="placeType">{translations["place.type"]}</label>
              <select
                id="placeType"
                value={selectedPlaceType}
                onChange={(e) => setSelectedPlaceType(e.target.value)}
                className="form-select"
              >
                <option value="">{translations["place.type.all"]}</option>
                <option value="gym">{translations["place.type.gym"]}</option>
                <option value="stadium">{translations["place.type.stadium"]}</option>
                <option value="sports-hall">{translations["place.type.sportsHall"]}</option>
                <option value="park">{translations["place.type.park"]}</option>
                <option value="court">{translations["place.type.court"]}</option>
                <option value="pool">{translations["place.type.pool"]}</option>
                <option value="field">{translations["place.type.field"]}</option>
                <option value="arena">{translations["place.type.arena"]}</option>
                <option value="club">{translations["place.type.club"]}</option>
              </select>
            </div>
          </div>

          <div className="filters">
            <h3>{translations["place.filters"]}</h3>
            <div className="filter-group">
              <label>
                <input type="checkbox" /> {translations["place.filter.openNow"]}
              </label>
              <label>
                <input type="checkbox" /> {translations["place.filter.freeEntry"]}
              </label>
              <label>
                <input type="checkbox" /> {translations["place.filter.accessible"]}
              </label>
            </div>
          </div>
        </div>

        <div className="map-area">
          {mapState && (
            <GeolocationWarning
              geolocationAllowed={mapState.geolocationAllowed}
              translations={translations}
            />
          )}
          <GeographicalMap
            places={places}
            onPlaceClick={handlePlaceClick}
            onMapStateChange={setMapState}
          />
        </div>
      </div>

      <div className="results-section">
        <h2>{translations["message.title.map"]}</h2>
        {places.length === 0 ? (
          <p className="no-results">{translations["place.noResults"]}</p>
        ) : (
          <div className="places-grid">
            {places.map((place) => (
              <div key={place.id} className="place-card" onClick={() => handlePlaceClick(place)}>
                <h3>{place.name}</h3>
                <p className="place-address">{place.address}</p>
                <p className="place-description">{place.description}</p>
                <div className="place-tags">
                  <span className="place-tag sport-tag">{place.sportType}</span>
                  <span className="place-tag type-tag">{place.placeType}</span>
                  {place.rating && (
                    <span className="place-tag rating-tag">★ {place.rating}</span>
                  )}
                  {place.isOpen !== undefined && (
                    <span className={`place-tag ${place.isOpen ? 'open-tag' : 'closed-tag'}`}>
                      {place.isOpen ? translations["place.tag.open"] : translations["place.tag.closed"]}
                    </span>
                  )}
                  {place.isFree && (
                    <span className="place-tag free-tag">{translations["place.tag.free"]}</span>
                  )}
                  {place.isAccessible && (
                    <span className="place-tag accessible-tag">{translations["place.tag.accessible"]}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default PlacePage;
